/**
 * Whether a sheet's attached alias drops its subtree before it re-expands,
 * and when a completed expansion certifies the tree it built: the per-alias
 * input and output signatures of the last certified expansion, and the
 * expansions running now (an overlapping one supersedes the one it
 * overtook, which then certifies nothing).
 *
 * **Package-internal**: `Sheet` owns one; every re-expansion of an attached
 * alias goes through `reexpand` (the sheet's listener, a template fan-out, a
 * timescale switch, a reset) and every expansion an alias template
 * materializes through `withExpansion` (`sheetInternals(sheet)
 * .withAliasExpansion`). The alias's template plan, its subtree and the
 * recompute after it come through `SheetAliasReexpansion.Host`; it names no
 * row type (the sheet instantiates `Alias` and `State`), so it sits outside
 * the rows' type knot.
 */

import type { Option } from "../core/option.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import { Tracing } from "@tento-core/tracing";

/**
 * How the sheet's re-expansion of an attached alias decides whether to drop
 * the alias's subtree before re-expanding it. `SheetAliasReexpansion.reexpand`'s
 * note has the three arms.
 */
export type ReexpandMode = "input" | "output" | "always";

/**
 * **Seam 13** — *why* an alias is being re-expanded, as opposed to `ReexpandMode`
 * which says only *which comparison decides the drop*. The two are not the same
 * question, and the mode alone cannot answer the one an operator asks: the
 * `"always"` arm exists precisely for an upstream input **neither signature can
 * observe** (`alias.default-weather` derives its child key from
 * `prefs.live.defaultWeatherLocation`), so a churn burst logged as `mode:"always"`
 * says a hidden input moved without ever saying which.
 *
 * The cause carries that name. `signature-check` subsumes the two comparing
 * modes (the mode is the comparison it ran); `hidden-input` carries the path of
 * the thing that moved — the declared `AliasTemplate.reexpandOn` pattern, i.e.
 * the preference key itself.
 */
export type ReexpandCause =
  | { readonly kind: "signature-check"; readonly mode: "input" | "output" }
  | { readonly kind: "hidden-input"; readonly path: string };

/** The default cause: an in-sheet change whose input signature decides the drop. */
export const REEXPAND_INPUT_SIGNATURE: ReexpandCause = {
  kind: "signature-check",
  mode: "input",
};

/** See the module note: a sheet's alias re-expansion admission, over its `Host`. */
export class SheetAliasReexpansion<Alias extends { readonly id: RowLocalId }, State> {
  readonly #host: SheetAliasReexpansion.Host<Alias, State>;
  // Per-alias signature of the inputs that determine its template-synthesized
  // children: the static inputs (props / timescale / attributes) plus, for
  // dynamic-tail templates, the RESOLVED tail projection (see
  // `AliasTemplateOps.expansionSignature`). `reexpand` skips the
  // destructive drop+rebuild when this is unchanged, so a data settle no longer
  // churns (re-inserts) an alias's renderer children — static OR dynamic-tail —
  // on every scroll frame / cell ingest. Only non-hydrated aliases are cached;
  // `resetAlias` invalidates the entry so a fork→reset still rebuilds.
  readonly #aliasReexpandSig = new Map<RowLocalId, string>();
  // Per-alias signature of the child specs the last expansion BUILT
  // (`AliasTemplateOps.childSpecSignature`). `reexpand` in
  // `"output"` mode — the timescale switch — drops a subtree only when the
  // specs it would build now differ from this. The input signature above
  // cannot make that call: it hashes `timescale`, which changed by
  // definition, while most templates' children never read it.
  readonly #aliasChildSpecSig = new Map<RowLocalId, string>();
  // The children the last certified expansion left under each alias (`inputMoved`'s repair check).
  readonly #aliasChildren = new Map<RowLocalId, readonly RowLocalId[]>();
  // Only active synchronous expansions; overlapping work cannot certify a mixed tree.
  readonly #activeAliasExpansions = new Map<RowLocalId, { superseded: boolean }>();

  constructor(host: SheetAliasReexpansion.Host<Alias, State>) {
    this.#host = host;
  }

  /**
   * Whether `alias`'s expansion inputs (`plan.inputSignature`: the static
   * inputs plus the resolved dynamic tail) differ from the ones its last
   * certified expansion consumed; `true` when it has none to compare. A
   * cell-value arrival under the alias asks this before re-expanding: it
   * moves no structure itself, but it is when a provider input that changed
   * since (a host's calendar list) is first observed.
   */
  inputMoved(alias: Alias): boolean {
    // A child the certified expansion built that is gone: re-expand, which
    // re-adds what is missing (the repair an arrival used to run on every
    // pass).
    const built = this.#aliasChildren.get(alias.id);
    if (built != null) {
      const now = new Set(this.#host.children(alias.id));
      if (built.some((id) => !now.has(id))) return true;
    }
    // A query: the signature is read without publishing anything (`Host.signature`).
    const sig = this.#host.signature(alias, this.#host.localState(alias));
    return !(sig != null && this.#aliasReexpandSig.get(alias.id) === sig);
  }

  /**
   * Re-expand the attached alias `alias` for `cause`, dropping its subtree
   * first only when the arm `cause` selects says its children changed.
   */
  reexpand(
    alias: Alias,
    cause: ReexpandCause,
  ): void {
    const host = this.#host;
    this.withExpansion(alias, (materialize) => {
      // For a non-hydrated alias the children are a pure function of the expansion
      // signature — the static inputs (props, timeScale, attributes) plus the
      // RESOLVED dynamic tail. When that is unchanged we must NOT drop the existing children:
      // `expandInstance` is idempotent (its per-child guard re-adds only what's
      // missing and emits no NewRowInserted for children that already exist), so the
      // unconditional drop+rebuild would only tear the identical subtree down and
      // re-insert it, churning the renderer rows on every scroll-driven data settle.
      // We therefore drop only when the signature actually changed (to handle child
      // REMOVALS) or when the alias is hydrated / template-unregistered (always reconcile);
      // otherwise we skip the drop and let the idempotent re-expansion below restore
      // any genuinely-missing children. `resetAlias` clears the cached signature so a
      // fork→reset still drops the real children.
      //
      // Three modes decide the drop (see `ReexpandMode`):
      //  - "input": the skip above — drop iff the expansion signature (the static
      //    inputs plus the RESOLVED dynamic tail, `AliasTemplateOps.expansionSignature`)
      //    changed.
      //  - "output": drop iff the DERIVED child specs differ from the ones the last
      //    expansion built. The timescale switch uses this: `timeScale` is an input
      //    that changed by definition, but most templates' children never read it
      //    (the calendar's are declared timescale-stable except for the month grid),
      //    so an input signature would drop — and re-fetch, and re-install — a
      //    subtree that comes back byte-identical under the same ids. Comparing
      //    outputs keeps that subtree, and every fetch coverage its rows hold.
      //  - "always": drop unconditionally. For callers whose input the signatures
      //    cannot see — `alias.default-weather` derives its child key from
      //    `prefs.live.defaultWeatherLocation`, so its `onAttach` listener fans out via
      //    `reexpandAliasesByTemplate` precisely because that hidden input changed —
      //    and for `resetAlias`, whose children are real forked rows that an
      //    identical spec list would otherwise keep in place.
      //
      // Seam 13: the drop is still decided by a `ReexpandMode`, but the mode is now
      // DERIVED from the cause rather than passed loose beside it — a hidden input
      // is, by definition, one no signature can see, so it is exactly the
      // "always" arm and cannot be paired with anything else by accident.
      const mode: ReexpandMode = cause.kind === "hidden-input" ? "always" : cause.mode;
      const localState = host.localState(alias);
      const plan = host.prepare(alias, localState);
      // Admission and materialization consume the same resolved static/tail specs.
      const sig = plan?.inputSignature ?? null;
      const outputSig = plan?.outputSignature ?? null;

      let drop: boolean;
      switch (mode) {
        case "always":
          drop = true;
          break;
        case "output":
          drop =
            outputSig === null ||
            this.#aliasChildSpecSig.get(alias.id) !== outputSig;
          break;
        case "input":
          drop = !(sig != null && this.#aliasReexpandSig.get(alias.id) === sig);
          break;
      }
      // Re-expansion sentinel: `dropped:true` bursts during a data settle are the
      // structural-churn signature (each drop recreates the alias's aggregate and
      // its fetch coverage — see `ListDataSource.generation`).
      Tracing.log("alias/reexpand", {
        template: host.template(alias),
        rowId: String(alias.id),
        dropped: drop,
        mode,
        hadSig: sig != null,
        priorInputPresent: this.#aliasReexpandSig.has(alias.id),
        inputMatches: sig != null && this.#aliasReexpandSig.get(alias.id) === sig,
        priorOutputPresent: this.#aliasChildSpecSig.has(alias.id),
        outputMatches: outputSig != null && this.#aliasChildSpecSig.get(alias.id) === outputSig,
        // Seam 13 — additive: existing keys (and the label) are untouched, so any
        // spec reading `mode` / `dropped` keeps reading them. `cause` names the
        // trigger and `path` (hidden inputs only) names the thing that moved, so a
        // `dropped:true` burst on the driver can be attributed to its source.
        cause: cause.kind,
        ...(cause.kind === "hidden-input" ? { path: cause.path } : {}),
      });

      if (drop) {
        const directChildren = [...host.children(alias.id)];
        for (const childId of directChildren) {
          host.dropSubtree(childId);
        }
        // A real drop invalidates the sheet-wide prefetch coverage: rebuilt
        // aggregates mint fresh (empty) SourceRows, and `ListDataSource`'s
        // fetch path delegates to `Sheet.ensureLoaded` — if the old coverage
        // survived, the new rows would short-circuit as "already fetched" and
        // never load. (Drops are rare post-signature: genuine tail changes and
        // `force` fan-outs only.)
        if (directChildren.length > 0) host.forgetLoadCoverage();
      }
      if (plan == null) host.expandInstance(alias, localState);
      else materialize(plan);
      // Under `batchStructural` the single coalesced drain recomputes every
      // computed source over final state; the per-alias recompute would run the
      // same full aggregate re-merges once per alias on intermediate state.
      if (!host.isBatching()) {
        host.recomputeDescendants(alias);
      }
    });
  }

  /**
   * Run `operation` as an expansion of `alias`: the plan it materializes
   * certifies the alias's signatures when the expansion started empty or
   * rebuilt the output the last certified one built, nothing overtook it,
   * the alias is still the sheet's and not hydrated. Anything else — and a
   * throw — clears them, so the next input pass rebuilds.
   */
  withExpansion(
    alias: Alias,
    operation: (materialize: (plan: SheetAliasReexpansion.Plan) => void) => void,
  ): void {
    const host = this.#host;
    const previous = this.#activeAliasExpansions.get(alias.id) ?? null;
    if (previous != null) previous.superseded = true;
    const active = { superseded: previous != null };
    this.#activeAliasExpansions.set(alias.id, active);
    const completedOutput = this.#aliasChildSpecSig.get(alias.id) ?? null;
    const staged: {
      signatures: Option<SheetAliasReexpansion.Signatures>;
      eligible: boolean;
      startedEmpty: boolean;
    } = {
      signatures: null,
      eligible: false,
      startedEmpty: false,
    };
    try {
      operation((plan) => {
        // Admission may have dropped the previous subtree. Capture this at
        // actual materialization, after that drop and before any child writes.
        const startedEmpty = host.children(alias.id).length === 0;
        staged.startedEmpty = startedEmpty;
        const signatures = plan.materialize();
        staged.signatures = signatures;
        staged.eligible = startedEmpty || completedOutput === signatures.outputSignature;
      });
      const signatures = staged.signatures;
      Tracing.log("alias/expansion-complete", {
        template: host.template(alias),
        rowId: String(alias.id),
        materialized: signatures != null,
        startedEmpty: staged.startedEmpty,
        eligible: staged.eligible,
        superseded: active.superseded,
        live: host.isLive(alias),
        hydrated: host.hydrated(alias),
        previousOutputPresent: completedOutput != null,
        outputMatches: signatures != null && completedOutput === signatures.outputSignature,
      });
      if (signatures != null && staged.eligible && !active.superseded &&
        host.isLive(alias) && !host.hydrated(alias)) {
        this.#aliasReexpandSig.set(alias.id, signatures.inputSignature);
        this.#aliasChildSpecSig.set(alias.id, signatures.outputSignature);
        this.#aliasChildren.set(alias.id, [...host.children(alias.id)]);
      } else {
        // Unapplied differing plans, retired aliases, zero-materialization and
        // overlapping work cannot certify the resulting tree.
        this.#aliasReexpandSig.delete(alias.id);
        this.#aliasChildSpecSig.delete(alias.id);
        this.#aliasChildren.delete(alias.id);
      }
    } catch (error) {
      // Partial work retains the original exception contract, but a later
      // input pass must not retain an obsolete successful baseline.
      this.#aliasReexpandSig.delete(alias.id);
      this.#aliasChildSpecSig.delete(alias.id);
      this.#aliasChildren.delete(alias.id);
      throw error;
    } finally {
      if (previous == null) this.#activeAliasExpansions.delete(alias.id);
      else this.#activeAliasExpansions.set(alias.id, previous);
    }
  }

  /** An expansion of `id` running now certifies nothing (a reset or a drop overtook it). */
  supersede(id: RowLocalId): void {
    const activeExpansion = this.#activeAliasExpansions.get(id) ?? null;
    if (activeExpansion != null) activeExpansion.superseded = true;
  }

  /** Forget `id`'s certified signatures: its next re-expansion drops and rebuilds. */
  forgetSignatures(id: RowLocalId): void {
    this.#aliasReexpandSig.delete(id);
    this.#aliasChildSpecSig.delete(id);
    this.#aliasChildren.delete(id);
  }
}

/** Types that go with `SheetAliasReexpansion`: `Host`, `Plan`, `Signatures`. */
export namespace SheetAliasReexpansion {
  /** What the admission reads and does through its sheet. */
  export interface Host<Alias, State> {
    /** The alias's local template state (`AliasTemplateOps.localState`). */
    localState(alias: Alias): State;
    /** The alias's prepared expansion, `null` when its template has none (`AliasTemplateOps.prepareExpansion`). */
    prepare(alias: Alias, state: State): Option<Plan>;
    /** The input signature `prepare` would answer, read without its side effect (it publishes the data address): `null` when the template has none. */
    signature(alias: Alias, state: State): Option<string>;
    /** Expand the alias's template without a plan (`AliasTemplateOps.expandInstance`). */
    expandInstance(alias: Alias, state: State): void;
    /** The alias's template name. */
    template(alias: Alias): string;
    /** Whether the alias is detached (`instance().hydrated`). */
    hydrated(alias: Alias): boolean;
    /** Whether the alias is still the sheet's row of its id. */
    isLive(alias: Alias): boolean;
    /** Row `id`'s children in tree order. */
    children(id: RowLocalId): readonly RowLocalId[];
    /** Drop row `id` and its subtree from the sheet. */
    dropSubtree(id: RowLocalId): void;
    /** Forget the sheet-wide prefetch's coverage (a real drop rebuilt the rows it covered). */
    forgetLoadCoverage(): void;
    /** Whether the tree is inside a structural batch (its drain recomputes). */
    isBatching(): boolean;
    /** Recompute the alias's computed descendants over the observed range. */
    recomputeDescendants(alias: Alias): void;
  }

  /** An expansion's signatures: its static/tail inputs and the child specs it builds. */
  export interface Signatures {
    readonly inputSignature: string;
    readonly outputSignature: string;
  }

  /** A prepared expansion (`AliasExpansionPlan`): its signatures, and its one materialization. */
  export interface Plan extends Signatures {
    materialize(): Signatures;
  }
}
